import { useState } from 'react';
import { useQuery } from 'react-query';

import { upliftHttpClient } from '@/services/http/clients/upliftHttpClient';
import { UpliftPaginatedList, UpliftUploadedFile } from '../abstractions/UpliftUploadedFile.interface';
import { DEFAULT_ERROR_MESSAGE } from '@/config/text.const';
import { useEffectExceptOnMount } from '@/hooks/useEffectExceptOnMount.hook';
import { UPLIFT_UPLOADED_FILES_PER_PAGE } from '@/config/common.config';
import { notificationService } from '@/services/notification/notification.client';

type Response = UpliftPaginatedList<UpliftUploadedFile>;
type PageConfig = {
  readonly pageIndex: number;
  readonly pageSize: number;
};

export function useGetUploadedUpliftFiles(onSuccess?: (data: Response) => void) {
  const [pageConfig, setPageConfig] = useState<PageConfig>({
    pageIndex: 1,
    pageSize: UPLIFT_UPLOADED_FILES_PER_PAGE
  });

  const loadData = async () => {
    const requestUrl = '/Documents';
    const response = await upliftHttpClient.get<Response>(requestUrl, {
      responseType: 'json',
      params: {
        pageIndex: pageConfig.pageIndex,
        pageSize: pageConfig.pageSize
      }
    });

    return response.data;
  };

  const { refetch, isFetching, data } = useQuery<Response>({
    queryKey: 'uploaded-uplift-files',
    queryFn: loadData,
    enabled: false,
    retry: false,
    onError: (err: any) => notificationService.open({
      title: 'Error',
      text: err?.message ?? DEFAULT_ERROR_MESSAGE,
      type: 'danger'
    }),
    onSuccess: (response) => onSuccess?.(response),
  });

  const getUploadedFiles = (pageIndex: number, pageSize: number) => {
    setPageConfig({ pageIndex, pageSize });
  };

  useEffectExceptOnMount(() => {
    refetch();
  }, [pageConfig]);

  return {
    getUploadedFiles,
    isLoading: isFetching,
    files: data?.items ?? [],
    totalPages: data?.totalPages ?? 0,
  } as const;
}
